import { useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { ErrorPage, PageLoader } from "@/components";
import { Header } from "@/app/modules/Navigation";

import { useCollection } from "@/hooks";
import { useResponsiveness, useUser } from "@/providers/context";

import { COURSE_VIEW_TEMPLATE, TEMPLATE_WEBSITE } from "@/const.ts";

import { CourseView } from "./components/CourseView.tsx";
import { DefaultCollection } from "./components/DefaultCollection.tsx";

interface CollectionDetailsProps {
  id: number;
}

export const CollectionDetails = ({ id }: CollectionDetailsProps) => {
  const { data, isLoading, isError } = useCollection(id);
  const { uid } = useUser();
  const { drawerMargin, transitionStyle } = useResponsiveness();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { isCollectionDripLocked } = useUser();

  useEffect(() => {
    if (data) {
      // If this collection is a website, open the website in a new tab and navigate back
      // This is needed as search results don't have full items, so it is not known if the collection is a website until this component is mounted
      if (data.TemplateId === TEMPLATE_WEBSITE && data.Properties?.Website) {
        window.open(data.Properties.Website, "_blank", "noopener,noreferrer");
        return;
      }

      // Same as above, but for Drip
      if (isCollectionDripLocked(data)) {
        // If coming from Search, navigate back and add isDripLocked state to display the drip locked modal
        if (state?.fromQuery) {
          navigate(`/search${state.fromQuery}`, {
            state: { isDripLocked: true },
          });
        } else {
          // Otherwise navigate home (e.g. if the URL was directly navigated to)
          navigate("/");
        }
      }
    }
  }, [data, state, navigate, isCollectionDripLocked]);

  if (isLoading) {
    return <PageLoader />;
  }

  if (!data || isError) {
    return <ErrorPage code={404} />;
  }

  if (!uid && data.Properties?.LockedUntilSignIn === "1") {
    return <Navigate to={`/sign-up?redirect=/collection/${id}`} replace />;
  }


  return (
    <div
      className="overflow-clip px-7 pb-8 text-item-primary"
      style={{
        marginRight: drawerMargin,
        transition: transitionStyle,
      }}
    >
      <Header />
      {data.TemplateId === COURSE_VIEW_TEMPLATE ? (
        <CourseView collection={data} />
      ) : (
        <DefaultCollection collection={data} />
      )}
    </div>
  );
};
